// Single chokepoint for talking to the PHP backend.
// BASE_URL = '/excelenta/'  →  apiBase = '/excelenta/api'

const BASE = ((import.meta.env.BASE_URL || '/').replace(/\/$/, '')) + '/api'

async function request(path, opts = {}) {
  const init = {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
    ...opts,
  }
  if (init.body && typeof init.body === 'object' && !(init.body instanceof FormData)) {
    init.headers['Content-Type'] = 'application/json'
    init.body = JSON.stringify(init.body)
  }
  let res
  try { res = await fetch(BASE + path, init) }
  catch (e) { const err = new Error('network'); err.code = 'network'; throw err }
  if (res.status === 204) return null
  let data = null
  try { data = await res.json() } catch { /* non-JSON */ }
  if (!res.ok) {
    const err = new Error(data?.error || 'server')
    err.code = data?.error || 'server'
    err.status = res.status
    // The whole body, verbatim. Some 422s carry detail the code alone cannot
    // express — `sms_too_long` needs {limit, length} and `sms_invalid_chars`
    // needs {chars} to say anything useful, and listing every such field here
    // would mean editing this chokepoint every time an endpoint adds one.
    err.data = data || {}
    if (data?.retry_after_seconds != null) err.retryAfterSeconds = data.retry_after_seconds
    if (data?.message)   err.detail    = data.message
    if (data?.exception) err.exception = data.exception
    if (data?.at)        err.at        = data.at
    throw err
  }
  return data
}

export const api = {
  health: () => request('/health'),
  org:    () => request('/org'),

  me: Object.assign(() => request('/me'), {
    update: (payload) => request('/me', { method: 'PATCH', body: payload }),
  }),

  auth: {
    requestCode: (payload) => request('/auth/request-code', { method: 'POST', body: payload }),
    verifyCode:  (payload) => request('/auth/verify-code',  { method: 'POST', body: payload }),
  },
  logout: () => request('/auth/logout', { method: 'POST' }),

  participants: {
    list:   () => request('/me/participants'),
    create: (payload)     => request('/me/participants',          { method: 'POST',   body: payload }),
    update: (id, payload) => request(`/me/participants/${id}`,    { method: 'PATCH',  body: payload }),
    // Participants are never deleted — only toggled.
    deactivate: (id) => request(`/me/participants/${id}/deactivate`, { method: 'POST' }),
    reactivate: (id) => request(`/me/participants/${id}/reactivate`, { method: 'POST' }),
  },

  courses: {
    list: () => request('/courses'),
    get:  (slug) => request(`/courses/${encodeURIComponent(slug)}`),
    enroll: (slug, payload) =>
      request(`/courses/${encodeURIComponent(slug)}/enroll`, { method: 'POST', body: payload }),
    // Account's participants + their status ON THIS COURSE (null = free to
    // pick, otherwise active / pending_payment / waitlisted).
    participants: (slug) => request(`/courses/${encodeURIComponent(slug)}/participants`),
    waitlist: (slug, payload) =>
      request(`/courses/${encodeURIComponent(slug)}/waitlist`, { method: 'POST', body: payload }),
  },

  enrollments: {
    list:   () => request('/me/enrollments'),
    cancel: (id) => request(`/me/enrollments/${id}/cancel`, { method: 'POST' }),
  },

  payments: {
    checkout: (id) => request(`/me/payments/${id}/checkout`, { method: 'POST' }),
  },

  // Monthly prepay: pay a whole calendar month up front and get the org's
  // prepay % off. `offer` is always computed LIVE on the server (never read
  // back from the send log), so a line settled in the meantime just vanishes.
  // `checkout` only SELECTS lines — the server recomputes every amount.
  prepay: {
    offer:    (month) => request(`/prepay/offer${month ? `?month=${encodeURIComponent(month)}` : ''}`),
    checkout: (month, lines) => request('/prepay/checkout', { method: 'POST', body: { month, lines } }),
  },

  // Site announcements behind the header bell. `list` is the ONLY thing the
  // bell polls; it already carries `has_new`, so there is no separate count
  // endpoint to keep in sync. `readAll` returns the stored marker — which is
  // the newest message's timestamp, NOT the moment of the click, so an
  // announcement published in the same second is not swallowed.
  announcements: {
    list:    () => request('/me/announcements'),
    readAll: () => request('/me/announcements/read-all', { method: 'POST' }),
  },

  teacher: {
    courses: () => request('/teacher/courses'),
    participants: (courseId) => request(`/teacher/courses/${courseId}/participants`),
    attendance: {
      roster: (courseId, date) =>
        request(`/teacher/courses/${courseId}/attendance${date ? `?date=${date}` : ''}`),
      add: (courseId, payload) =>
        request(`/teacher/courses/${courseId}/attendance`, { method: 'POST', body: payload }),
      remove: (courseId, payload) =>
        request(`/teacher/courses/${courseId}/attendance`, { method: 'DELETE', body: payload }),
      byDate: (date) => request(`/teacher/attendance${date ? `?date=${date}` : ''}`),
    },
  },

  uploads: {
    // multipart — do NOT set Content-Type; the browser adds the boundary.
    upload: (file) => {
      const fd = new FormData()
      fd.append('file', file)
      return request('/admin/uploads', { method: 'POST', body: fd })
    },
  },

  admin: {
    users: {
      list:     () => request('/admin/users'),
      get:      (id)          => request(`/admin/users/${id}`),
      teachers: ()            => request('/admin/users/teachers'),
      create:   (payload)     => request('/admin/users', { method: 'POST', body: payload }),
      update:   (id, payload) => request(`/admin/users/${id}`, { method: 'PATCH', body: payload }),
      delete:   (id)          => request(`/admin/users/${id}`, { method: 'DELETE' }),
    },
    participants: {
      create:     (userId, payload) => request(`/admin/users/${userId}/participants`, { method: 'POST', body: payload }),
      update:     (id, payload)     => request(`/admin/participants/${id}`, { method: 'PATCH', body: payload }),
      deactivate: (id) => request(`/admin/participants/${id}/deactivate`, { method: 'POST' }),
      reactivate: (id) => request(`/admin/participants/${id}/reactivate`, { method: 'POST' }),
    },
    categories: {
      list:   () => request('/admin/categories'),
      create: (payload)     => request('/admin/categories',        { method: 'POST',  body: payload }),
      update: (id, payload) => request(`/admin/categories/${id}`,  { method: 'PATCH', body: payload }),
    },
    courses: {
      list:       () => request('/admin/courses'),
      get:        (id)          => request(`/admin/courses/${id}`),
      create:     (payload)     => request('/admin/courses',       { method: 'POST',  body: payload }),
      update:     (id, payload) => request(`/admin/courses/${id}`, { method: 'PATCH', body: payload }),
      activate:   (id) => request(`/admin/courses/${id}/activate`,   { method: 'POST' }),
      deactivate: (id) => request(`/admin/courses/${id}/deactivate`, { method: 'POST' }),
      participants:   (id) => request(`/admin/courses/${id}/participants`),
      addParticipant: (id, payload) =>
        request(`/admin/courses/${id}/participants`, { method: 'POST', body: payload }),
    },
    enrollments: {
      setStatus: (id, status) =>
        request(`/admin/enrollments/${id}`, { method: 'PATCH', body: { status } }),
    },
    waitlist: {
      list:    () => request('/admin/waitlist'),
      promote: (payload) => request('/admin/waitlist/promote', { method: 'POST', body: payload }),
    },
    // Monthly attendance billing, run by hand. `preview` is a dry run (writes
    // nothing); `run` actually invoices. Same job as the 1st-of-the-month cron.
    billing: {
      preview: () => request('/admin/billing/monthly'),
      run:     () => request('/admin/billing/monthly', { method: 'POST' }),
    },
    // Prepay offers — the same job as the 1st-of-the-month cron (0 7 1 * *).
    // `preview` is a dry run (sends nothing) and also returns how the month
    // converted so far; `run` mails the offers for real.
    prepay: {
      preview: (month) => request(`/admin/prepay${month ? `?month=${encodeURIComponent(month)}` : ''}`),
      run:     (month) => request(`/admin/prepay${month ? `?month=${encodeURIComponent(month)}` : ''}`, { method: 'POST' }),
    },
    // Discounts: coupon codes AND automatic conditions, in ONE ordered list.
    // `reorder` posts the full ordered id list — sort_order ASC decides which
    // discount wins, and only the first match is ever applied.
    discounts: {
      list:    () => request('/admin/discounts'),
      get:     (id)          => request(`/admin/discounts/${id}`),
      create:  (payload)     => request('/admin/discounts', { method: 'POST',  body: payload }),
      update:  (id, payload) => request(`/admin/discounts/${id}`, { method: 'PATCH', body: payload }),
      remove:  (id)          => request(`/admin/discounts/${id}`, { method: 'DELETE' }),
      reorder: (ids)         => request('/admin/discounts/reorder', { method: 'POST', body: { ids } }),
      redemptions: (id)      => request(`/admin/discounts/${id}/redemptions`),
      // Redemption log — every usage of every discount, newest first.
      log: ({ discount_id, user_id, course_id, kind, from, to, q, limit } = {}) => {
        const p = new URLSearchParams()
        if (discount_id) p.set('discount_id', discount_id)
        if (user_id)     p.set('user_id', user_id)
        if (course_id)   p.set('course_id', course_id)
        if (kind)        p.set('kind', kind)
        if (from)        p.set('from', from)
        if (to)          p.set('to', to)
        if (q)           p.set('q', q)
        if (limit)       p.set('limit', limit)
        const qs = p.toString()
        return request(`/admin/discounts/log${qs ? `?${qs}` : ''}`)
      },
    },
    // Announcements — admin only, ONE-SHOT: there is no update and no delete.
    // An announcement is a record of something already in people's inboxes;
    // `preview` (writes nothing) is what replaces "edit".
    // `queue` is the manual twin of the announcements-queue cron: it delivers
    // one batch of pending email/SMS rows right now.
    announcements: {
      list:    (limit) => request(`/admin/announcements${limit ? `?limit=${limit}` : ''}`),
      get:     (id)      => request(`/admin/announcements/${id}`),
      preview: (payload) => request('/admin/announcements/preview', { method: 'POST', body: payload }),
      send:    (payload) => request('/admin/announcements',         { method: 'POST', body: payload }),
      queue:   ()        => request('/admin/announcements/queue',   { method: 'POST' }),
    },
    payments: {
      list:   (status) => request(`/admin/payments${status ? `?status=${status}` : ''}`),
      get:    (id) => request(`/admin/payments/${id}`),
      cancel: (id) => request(`/admin/payments/${id}/cancel`, { method: 'POST' }),
    },
    presences: {
      list: ({ course_id, user_id, from, to } = {}) => {
        const p = new URLSearchParams()
        if (course_id) p.set('course_id', course_id)
        if (user_id)   p.set('user_id', user_id)
        if (from)      p.set('from', from)
        if (to)        p.set('to', to)
        const qs = p.toString()
        return request(`/admin/presences${qs ? `?${qs}` : ''}`)
      },
    },
    org: {
      get:    () => request('/admin/org'),
      update: (payload) => request('/admin/org', { method: 'PATCH', body: payload }),
    },
  },
}
